import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Per-page SEO head — title, description, canonical, OG, JSON-LD.
 * Use on every public route so each URL has a distinct entry in Google.
 *
 * @param {string} title - page title (will be appended with " | Rooam Sports Network")
 * @param {string} description - 1–2 sentence summary (~150 chars ideal)
 * @param {string} path - canonical path relative to root, e.g. "/events" or "/event/abc"
 * @param {string} [image] - absolute or root-relative og:image URL
 * @param {object} [jsonLd] - optional schema.org JSON-LD object
 * @param {boolean} [noindex] - set true for pages we don't want indexed
 */
export const SeoHead = ({ title, description, path = '/', image, jsonLd, noindex = false }) => {
  const SITE = 'https://rooamsports.network';
  const url = `${SITE}${path}`;
  const fullTitle = title ? `${title} | Rooam Sports Network` : 'Rooam Sports Network';
  const imgUrl = image
    ? (image.startsWith('http') ? image : `${SITE}${image.startsWith('/') ? image : '/' + image}`)
    : `${SITE}/og-image.png`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={imgUrl} />
      <meta property="og:site_name" content="Rooam Sports Network" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={imgUrl} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SeoHead;
